import React, { useState, useEffect } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import CustomDialog from "../../../components/common/CustomDialog";
import CustomButton from "../../../components/common/CustomButton/CustomButton";
import TemplateFormFields from "./TemplateFormFields";
import { GetInvoiceTemplates, AddInvoiceTemplate, UpdateInvoiceTemplate, getAllCategoriesUrl, getSubCategoriesByIdUrl, UpdateInvoiceTemplateLogo } from "../../../utils/urls";
import axios from "axios";

const TemplatesTab = () => {
  const [templates, setTemplates] = useState([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [menuOpen, setMenuOpen] = useState(null);
  const [search, setSearch] = useState("");
  const [expandedSection, setExpandedSection] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, data: null });
  const [isLoading, setIsLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categoryList, setCategoryList] = useState([]);
  const [subCategoryList, setSubCategoryList] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [isViewMode, setIsViewMode] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [logoFile, setLogoFile] = useState(null);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(getAllCategoriesUrl + "?is_all=true");
      if (response.data?.success) {
        const data = response.data?.data;
        const activeCategories = data.filter((cat) => cat.is_active);
        setCategoryList(activeCategories);
      } else {
        setCategoryList([]);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      setCategoryList([]);
      setError("Failed to load categories. Please try again.");
    }
  };

  const fetchSubCategories = async () => {
    try {
      if (selectedCategoryId) {
        const response = await axios.get(getSubCategoriesByIdUrl + `/${selectedCategoryId}`);
        if (response.data?.success) {
          const data = response.data?.data;
          const activeSubCategories = data.filter((sub) => sub.is_active);
          setSubCategoryList(activeSubCategories);
        } else {
          setSubCategoryList([]);
        }
      } else {
        setSubCategoryList([]);
      }
    } catch (error) {
      console.error("Error fetching subcategories:", error);
      setSubCategoryList([]);
      setError("Failed to load subcategories. Please try again.");
    }
  };

  const fetchTemplates = async () => {
    setFetchLoading(true);
    setError(null);
    try {
      const queryParams = new URLSearchParams({
        page_number: page,
        limit: limit,
        search: search,
      }).toString();
      const response = await fetch(`${GetInvoiceTemplates}?${queryParams}`);
      const result = await response.json();
      if (result.success) {
        const formattedTemplates = result.data.map(template => ({
          id: template.id,
          title: template.title,
          imageUrl: template?.invoice_template_id?.preview_image || "https://via.placeholder.com/150",
          category_title: template.category[0]?.title || "",
          category_id: template.category[0]?.category_id || "",
          sub_category_title: template.sub_category[0]?.title || "",
          sub_category_id: template.sub_category[0]?.sub_category_id || "",
          company_name: template.company_header_config.company_name || "",
          upload_logo: template.logo_url || "",
          company_address: template.company_header_config.company_address || "",
          phone_number: template.company_header_config.phone_number || "",
          email_address: template.company_header_config.email_address || "",
          gstin_number: template.company_header_config.gstin_number || "",
          invoice_title: template.invoice_info_config.invoice_title || "",
          invoice_prefix: template.invoice_info_config.invoice_prefix || "",
          invoice_date_format: template.invoice_info_config.invoice_date_format || "",
          customer_name: template.bill_to_config.customer_name || false,
          billing_address: template.bill_to_config.billing_address || false,
          phone_number_bill: template.bill_to_config.phone_number || false,
          email_address_bill: template.bill_to_config.email_address || false,
          gstin_number_bill: template.bill_to_config.gstin_number || false,
          lead_table_preview_config: template.lead_table_preview_config?.map(col => col.column_name) || [],
          is_enable_taxes: template.tax_summary_config.is_enable_taxes || false,
          cgst_enable: template.tax_summary_config.CGST.is_enable || false,
          cgst_value: template.tax_summary_config.CGST.value || 0,
          sgst_enable: template.tax_summary_config.SGST.is_enable || false,
          sgst_value: template.tax_summary_config.SGST.value || 0,
          igst_enable: template.tax_summary_config.IGST.is_enable || false,
          igst_value: template.tax_summary_config.IGST.value || 0,
          is_show_subtotal: template.totals_section_config.is_show_subtotal || false,
          is_show_total_tax_amount: template.totals_section_config.is_show_total_tax_amount || false,
          is_show_grand_total: template.totals_section_config.is_show_grand_total || false,
          bank_name: template.bank_details_config.bank_name || false,
          account_number: template.bank_details_config.account_number || false,
          ifsc_code: template.bank_details_config.ifsc_code || false,
          uip_id: template.bank_details_config.uip_id || false,
          payment_terms_conditions: template.terms_conditions_config.payment_terms || "",
          thank_you_message: template.terms_conditions_config.thank_you_message || "",
        }));
        setTemplates(formattedTemplates);
      } else {
        throw new Error(result.message || 'Failed to fetch templates');
      }
    } catch (error) {
      setError('Failed to load templates. Please try again.');
      console.error('Fetch templates error:', error);
    } finally {
      setFetchLoading(false);
    }
  };

  const validationSchema = Yup.object({
    title: Yup.string().required("Template Name is required"),
    category_id: Yup.string().required("Category is required"),
    sub_category_id: Yup.string().required("Sub Category is required"),
    company_name: Yup.string().required("Company Name is required"),
    company_address: Yup.string().required("Company Address is required"),
    phone_number: Yup.string().required("Phone Number is required"),
    email_address: Yup.string().email("Invalid email address").required("Email Address is required"),
    invoice_title: Yup.string().required("Invoice Title is required"),
    invoice_prefix: Yup.string().required("Invoice Prefix is required"),
    invoice_date_format: Yup.string().required("Invoice Date Format is required"),
    lead_table_preview_config: Yup.array()
      .of(Yup.string())
      .min(1, "At least one column must be selected")
      .required("Column selection is required"),
    cgst_value: Yup.number().when("cgst_enable", (cgst_enable, schema) =>
      cgst_enable
        ? schema
            .min(0, "CGST Value must be non-negative")
            .required("CGST Value is required")
        : schema.notRequired()
    ),
    sgst_value: Yup.number().when("sgst_enable", (sgst_enable, schema) =>
      sgst_enable
        ? schema
            .min(0, "SGST Value must be non-negative")
            .required("SGST Value is required")
        : schema.notRequired()
    ),
    igst_value: Yup.number().when("igst_enable", (igst_enable, schema) =>
      igst_enable
        ? schema
            .min(0, "IGST Value must be non-negative")
            .required("IGST Value is required")
        : schema.notRequired()
    ),
    payment_terms_conditions: Yup.string().required("Payment Terms are required"),
    thank_you_message: Yup.string().required("Thank You Message is required"),
  });

  const initialValues = {
    id: "",
    title: "",
    category_id: "",
    category_title: "",
    sub_category_id: "",
    sub_category_title: "",
    company_name: "",
    upload_logo: "",
    company_address: "",
    phone_number: "",
    email_address: "",
    gstin_number: "",
    invoice_title: "",
    invoice_prefix: "",
    invoice_date_format: "",
    customer_name: true,
    billing_address: true,
    phone_number_bill: true,
    email_address_bill: true,
    gstin_number_bill: true,
    lead_table_preview_config: [],
    is_enable_taxes: false,
    cgst_enable: false,
    cgst_value: 0,
    sgst_enable: false,
    sgst_value: 0,
    igst_enable: false,
    igst_value: 0,
    is_show_subtotal: true,
    is_show_total_tax_amount: true,
    is_show_grand_total: true,
    bank_name: true,
    account_number: true,
    ifsc_code: true,
    uip_id: true,
    payment_terms_conditions: "",
    thank_you_message: "",
  };

  useEffect(() => {
    fetchCategories();
    fetchTemplates();
  }, [page, search]);

  useEffect(() => {
    fetchSubCategories();
  }, [selectedCategoryId]);

  const handleEdit = (template) => {
    setEditData(template);
    setIsViewMode(false);
    setIsFormOpen(true);
    setMenuOpen(null);
    setSelectedCategoryId(template.category_id || null);
    setLogoFile(null);
  };

  const handleView = (template) => {
    setEditData(template);
    setIsViewMode(true);
    setIsFormOpen(true);
    setMenuOpen(null);
    setSelectedCategoryId(template.category_id || null);
    setLogoFile(null);
  };

  const handleDelete = (id) => {
    setConfirmDialog({ open: true, data: { id } });
    setMenuOpen(null);
  };

  const handleConfirmDelete = async () => {
    setIsLoading(true);
    try {
      await deleteTemplate(confirmDialog.data.id);
    } catch (error) {
      console.error("Error deleting template:", error);
      setError('Failed to delete template. Please try again.');
    } finally {
      setIsLoading(false);
      setConfirmDialog({ open: false, data: null });
    }
  };

  const deleteTemplate = async (id) => {
    console.log(`Placeholder: Deleting template with ID ${id}`);
  };

  const uploadLogo = async (templateId, file) => {
    if (!file || !templateId) return null;

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('id', templateId);

      const response = await fetch(UpdateInvoiceTemplateLogo, {
        method: 'PUT',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to upload logo');
      }

      const result = await response.json();
      return result.logo_url;
    } catch (error) {
      console.error('Logo upload error:', error);
      setError('Failed to upload logo. Please try again.');
      return null;
    }
  };

  const mapFormValuesToApiPayload = (values) => ({
    ...(editData && { id: values.id }),
    title: values.title,
    logo_url: values.upload_logo || null,
    category: [values.category_id],
    sub_category: [values.sub_category_id],
    company_header_config: {
      company_name: values.company_name,
      company_address: values.company_address,
      phone_number: values.phone_number,
      email_address: values.email_address,
      gstin_number: values.gstin_number,
    },
    invoice_info_config: {
      invoice_title: values.invoice_title,
      invoice_prefix: values.invoice_prefix,
      invoice_date_format: values.invoice_date_format,
    },
    bill_to_config: {
      customer_name: values.customer_name,
      billing_address: values.billing_address,
      phone_number: values.phone_number_bill,
      email_address: values.email_address_bill,
      gstin_number: values.gstin_number_bill,
    },
    lead_table_preview_config: values.lead_table_preview_config.map(column => ({ column_name: column })),
    tax_summary_config: {
      is_enable_taxes: values.is_enable_taxes,
      CGST: {
        is_enable: values.cgst_enable,
        value: Number(values.cgst_value) || 0,
      },
      SGST: {
        is_enable: values.sgst_enable,
        value: Number(values.sgst_value) || 0,
      },
      IGST: {
        is_enable: values.igst_enable,
        value: Number(values.igst_value) || 0,
      },
    },
    totals_section_config: {
      is_show_subtotal: values.is_show_subtotal,
      is_show_total_tax_amount: values.is_show_total_tax_amount,
      is_show_grand_total: values.is_show_grand_total,
    },
    bank_details_config: {
      bank_name: values.bank_name,
      account_number: values.account_number,
      ifsc_code: values.ifsc_code,
      uip_id: values.uip_id,
    },
    terms_conditions_config: {
      payment_terms: values.payment_terms_conditions,
      thank_you_message: values.thank_you_message,
    },
  });

  return (
    <div className="p-4">
      <div className="flex justify-between mb-4">
        <input
          type="text"
          placeholder="Search here..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-64 p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <CustomButton
          onClick={() => {
            setEditData(null);
            setIsViewMode(false);
            setIsFormOpen(true);
            setSelectedCategoryId(null);
            setLogoFile(null);
          }}
          label="Create Invoice Template"
          variant="contained"
          disabled={fetchLoading}
        />
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      {fetchLoading ? (
        <div className="text-center">Loading templates...</div>
      ) : templates.length === 0 ? (
        <div className="text-center text-gray-500">No templates found.</div>
      ) : (
        <div className="grid grid-cols-5 gap-6">
          {templates
            .filter(template => 
              template.title.toLowerCase().includes(search.toLowerCase()) ||
              template.category_title.toLowerCase().includes(search.toLowerCase()) ||
              template.sub_category_title.toLowerCase().includes(search.toLowerCase())
            )
            .map(template => (
              <div key={template.id} className="relative border rounded-lg p-3 w-[180px]">
                <div className="absolute top-2 right-2">
                  <button onClick={() => setMenuOpen(menuOpen === template.id ? null : template.id)}>
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                    </svg>
                  </button>
                  {menuOpen === template.id && (
                    <div className="absolute right-0 mt-2 w-32 bg-white border rounded-lg shadow-lg z-10">
                      <button 
                        onClick={() => handleView(template)} 
                        className="flex items-center gap-2 w-full px-4 py-2 text-left hover:bg-gray-100"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        View
                      </button>
                      <button 
                        onClick={() => handleEdit(template)} 
                        className="flex items-center gap-2 w-full px-4 py-2 text-left hover:bg-gray-100"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.536L16.732 3.732z" />
                        </svg>
                        Edit
                      </button>
                    </div>
                  )}
                </div>
                <img src={template?.imageUrl} alt={template.title} className="w-full h-40 object-cover mb-2" />
                <h3 className="text-center font-medium text-sm">{template.title}</h3>
              </div>
            ))}
        </div>
      )}

      <CustomDialog
        open={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setEditData(null);
          setIsViewMode(false);
          setError(null);
          setSelectedCategoryId(null);
          setSubCategoryList([]);
          setLogoFile(null);
        }}
        title=""
        className="!p-0"
      >
        <div className="w-[900px]">
          <div className="flex justify-between items-center bg-[#0051AD] px-4 py-3 rounded-t-lg">
            <span className="text-sm font-medium text-white">
              {isViewMode ? "View Template" : editData ? "Edit Template" : "Add New Template"}
            </span>
            <button 
              onClick={() => {
                setIsFormOpen(false);
                setEditData(null);
                setIsViewMode(false);
                setError(null);
                setSelectedCategoryId(null);
                setSubCategoryList([]);
                setLogoFile(null);
              }} 
              className="text-white hover:text-gray-200"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <Formik
            initialValues={editData || initialValues}
            validationSchema={validationSchema}
            onSubmit={async (values, { setSubmitting }) => {
              if (isViewMode) {
                setIsFormOpen(false);
                setEditData(null);
                setIsViewMode(false);
                setSelectedCategoryId(null);
                setSubCategoryList([]);
                setLogoFile(null);
                return;
              }

              setIsLoading(true);
              setError(null);
              try {
                const payload = mapFormValuesToApiPayload(values);
                const url = editData ? UpdateInvoiceTemplate : AddInvoiceTemplate;
                const method = editData ? 'PUT' : 'POST';

                const response = await fetch(url, {
                  method,
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify(payload),
                });

                if (!response.ok) {
                  throw new Error('Failed to save template');
                }

                const result = await response.json();
                const templateId = result.data?.id || values.id;

                if (logoFile) {
                  console.log("uploading logo");
                  await uploadLogo(templateId, logoFile);
                }

                // Refetch templates to get the latest data
                await fetchTemplates();

                setIsFormOpen(false);
                setEditData(null);
                setIsViewMode(false);
                setSelectedCategoryId(null);
                setSubCategoryList([]);
                setLogoFile(null);
              } catch (error) {
                setError('Failed to save template. Please try again.');
                console.error('Save template error:', error);
              } finally {
                setIsLoading(false);
                setSubmitting(false);
              }
            }}
            enableReinitialize
          >
            {({ values, setFieldValue, isSubmitting, errors, isValid }) => (
              <Form className="p-4">
                {error && (
                  <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">
                    {error}
                  </div>
                )}
                {!isValid && (
                  <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">
                    Please fill in all required fields before submitting.
                  </div>
                )}
                <TemplateFormFields
                  expandedSection={expandedSection}
                  setExpandedSection={setExpandedSection}
                  values={values}
                  setFieldValue={(field, value) => {
                    if (field === "category_id") {
                      console.log('Category changed to:', value);
                      setSelectedCategoryId(value);
                      setFieldValue("sub_category_id", "");
                      setFieldValue("sub_category_title", "");
                    }
                    setFieldValue(field, value);
                  }}
                  categories={categoryList}
                  subCategories={subCategoryList}
                  templateId={editData?.id}
                  isEditMode={!!editData}
                  // isViewMode={isViewMode}
                  isViewMode={false}
                  formErrors={errors}
                  logoFile={logoFile}
                  setLogoFile={setLogoFile}
                />
                <div className="flex justify-end gap-3 mt-4">
                  <CustomButton
                    type="button"
                    onClick={() => {
                      setIsFormOpen(false);
                      setEditData(null);
                      setIsViewMode(false);
                      setError(null);
                      setSelectedCategoryId(null);
                      setSubCategoryList([]);
                      setLogoFile(null);
                    }}
                    label="Cancel"
                    variant="outlined"
                    disabled={isSubmitting}
                  />
                  <CustomButton
                    type="submit"
                    label={isViewMode ? "Close" : isLoading ? "Saving..." : "Save"}
                    variant="contained"
                    disabled={isSubmitting || isLoading || !isValid}
                  />
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </CustomDialog>

      <CustomDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, data: null })}
        title="Are You Sure?"
        titleClassName="text-center"
        className=""
      >
        <div className="w-[400px] text-center">
          <p className="text-gray-600 text-left border-b pb-4">
            Are you sure you want to delete this template?
          </p>
          <div className="flex justify-between gap-3 mt-4">
            <button
              className="py-2 px-4 bg-gray-200 font-semibold text-base w-[170px] rounded-md"
              onClick={() => setConfirmDialog({ open: false, data: null })}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              className="py-2 px-4 bg-blue-500 w-[170px] font-semibold text-base text-white rounded-md disabled:opacity-70"
              onClick={handleConfirmDelete}
              disabled={isLoading}
            >
              {isLoading ? "Processing..." : "Confirm"}
            </button>
          </div>
        </div>
      </CustomDialog>
    </div>
  );
};

export default TemplatesTab;